<script lang="ts" setup>
import urlJoin from 'url-join'
import type { Breadcrumb } from '~/types/Product'
import buildCategoryUrl from '~/utils/filters/buildCategoryUrl'
import getDefaultLocale from '~/utils/locale/getDefaultLocale'

const expanded = ref<Boolean>(false)
const { appUrl } = getDefaultLocale()

const props = defineProps<{
  breadcrumbs?: Breadcrumb[]
  productBreadcrumb?: Breadcrumb
  notActive?: boolean
}>()

const expand = () => {
  expanded.value = true
}

const productSchemaBreadcrumb = props.productBreadcrumb
  ? [
      {
        '@type': 'ListItem',
        position: props.breadcrumbs?.length ?? 0,
        name: props.productBreadcrumb?.translation ?? '',
        item: urlJoin(appUrl, props.productBreadcrumb?.id as string),
      },
    ]
  : []

const breadcrumbsJsonldSchemaItems = (
  props.breadcrumbs?.map((breadcrumb, index) => ({
    '@type': 'ListItem',
    position: index,
    name: breadcrumb.translation ?? '',
    item: urlJoin(
      appUrl,
      // @ts-expect-error
      buildCategoryUrl(breadcrumb.id, breadcrumb.isFullPath).path
    ),
  })) ?? []
).concat(productSchemaBreadcrumb)

// @ts-ignore
useJsonld(() => ({
  '@context': 'https://schema.org',
  '@type': 'BreadcrumbList',
  itemListElement: breadcrumbsJsonldSchemaItems,
}))
</script>

<template>
  <div v-if="props.breadcrumbs?.length" class="breadcrumbs-component">
    <div class="breadcrumbs py-4 lg:pt-5" :class="{ expanded: expanded }">
      <ol class="breadcrumbs-items">
        <li
          class="breadcrumb-item !overflow-visible ellipsis"
          :class="{ hidden: props.breadcrumbs?.length <= 2 }"
          @click="expand">
          ...
        </li>
        <li
          v-for="(item, index) in props.breadcrumbs"
          :key="index"
          class="breadcrumb-item"
          :class="{ '-active': index === props.breadcrumbs.length - 1 && !props.notActive }">
          <NuxtLink :to="buildCategoryUrl(item.id, item.isFullPath)">
            {{ item.translation }}
          </NuxtLink>
        </li>
      </ol>
    </div>
  </div>
</template>

<style lang="scss">
.breadcrumbs-component {
  .hidden {
    display: none;
  }

  .breadcrumbs {
    padding-left: 1rem;
    padding-right: 1rem;
    font-size: 0.75rem;
    display: flex;
    text-overflow: ellipsis;
    white-space: nowrap;
    overflow: hidden;
    margin: 0;

    .breadcrumb-item {
      margin: 0;
      padding-left: 0.5rem;
      display: flex;
      text-overflow: ellipsis;
      white-space: nowrap;
      overflow: hidden;
      line-height: 1.9;

      &:first-child {
        padding-left: 0;
      }

      &.-active a {
        color: theme('colors.brand.primary') !important;
      }

      a {
        @apply text-neutral3 dark:text-dmText1;

        display: inline;
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
        font-family: 'Roboto';
        font-style: normal;
        font-weight: 500;
        font-size: 12px;
        line-height: 120%;
        letter-spacing: 0.025em;
      }
    }

    ol {
      width: 100%;
      margin-bottom: 0;
    }
    .breadcrumbs-items {
      display: inherit;
      li {
        display: none;

        & + .breadcrumb-item::before {
          padding-right: 0.5rem;
        }
        &:not(:first-of-type):before {
          @apply text-gray5 dark:text-dmText1;

          font-weight: 900;
          font-size: 0.75rem;
          font-family: theme('vars.fontAwesome');
          content: '\f054' !important;
        }
        &:first-child {
          & + li:nth-child(2) {
            padding-left: 0;
          }
          & + li:before {
            content: normal !important;
          }
          &:not(.hidden) {
            display: inline;
            & + li:nth-child(2) {
              padding-left: 1rem;
            }
          }
        }
        &:nth-last-child(-n + 2) {
          display: inline;
          position: relative;
        }
      }
    }
    &.expanded {
      & > ol {
        flex-wrap: wrap;
        display: initial;
      }
      & > .breadcrumbs-items {
        li {
          display: inline;
          white-space: normal;
          overflow: visible;
          & + .breadcrumb-item::before {
            display: none;
          }
          & + .breadcrumb-item::after {
            padding-left: 0.5rem;
          }
          &:not(:first-of-type, :last-child):after {
            @apply text-neutral3 dark:text-dmText1;

            display: inline-block;
            font-weight: 900;
            font-size: 0.75rem;
            font-family: theme('vars.fontAwesome');
            content: '\f054' !important;
          }

          a {
            white-space: normal;
          }

          &:nth-child(2):before {
            display: none;
          }
        }
        & > li:first-child {
          display: inline;
          & + li:nth-child(2) {
            padding-left: 0;
          }
        }

        li:first-child {
          display: none;
        }
      }
    }
  }

  @screen sm {
    .breadcrumbs {
      .breadcrumb-item {
        padding-left: 1rem;
      }
      & > ol.breadcrumbs-items {
        flex-wrap: wrap;
        display: initial;
      }
      & > .breadcrumbs-items {
        li {
          display: inline;
          white-space: normal;
          overflow: visible;
          & + .breadcrumb-item::before {
            display: none;
          }
          & + .breadcrumb-item::after {
            padding-left: 1rem;
          }
          &:not(:first-of-type, :last-child):after {
            @apply text-neutral3 dark:text-dmText1;

            display: inline-block;
            font-weight: 900;
            font-size: 0.75rem;
            font-family: theme('vars.fontAwesome');
            content: '\f054' !important;
          }

          a {
            white-space: normal;
            font-size: 14px;
          }

          &:nth-child(2):before {
            display: none;
          }
        }
        & > li:first-child {
          display: none;
        }

        li:first-child:not(.hidden) {
          & {
            display: none;
            padding-left: 0;
          }

          & + li:nth-child(2) {
            padding-left: 0;
          }
        }
      }
    }
  }

  @media screen and (min-width: 1025px) {
    .breadcrumbs {
      padding-left: 0;
      padding-right: 0;
    }
  }
}

.breadcrumbs-component {
  .breadcrumb-item {
    a {
      font-weight: 400 !important;
    }
  }
}
</style>
